import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { ModalProps } from './Modal.types';

const modalVariants = cva(
  [
    'relative mx-auto flex max-h-[85vh] flex-col rounded-lg bg-(--color-bg-primary) shadow-xl',
    'animate-in fade-in zoom-in-95 duration-200',
  ],
  {
    variants: {
      size: {
        sm: 'w-full max-w-sm',
        md: 'w-full max-w-lg',
        lg: 'w-full max-w-2xl',
        full: 'w-full max-w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    },
    [onClose, closeOnEscape],
  );

  // Listener de teclado: re-vincula quando o handler muda; NÃO mexe em foco aqui.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  // Scroll lock + restauração de foco: depende só de `open`, então roda apenas
  // nas transições abrir/fechar. (Se dependesse de `handleKeyDown`, a limpeza
  // rodaria a cada render — quando o `onClose` do pai muda de identidade — e
  // devolveria o foco ao elemento anterior, fazendo o input perder o foco a cada
  // tecla digitada.)
  useEffect(() => {
    if (!open) return;
    previousActiveElement.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    };
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(modalVariants({ size }))}
      >
        {title && (
          <div className="border-(--color-border) flex items-center justify-between border-b px-6 py-4">
            <h2
              id={titleId}
              className="text-(--color-text-primary) text-lg font-semibold"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="focus:ring-primary-500 text-(--color-text-secondary) hover:text-(--color-text-primary) rounded-md p-1 focus:outline-none focus:ring-2"
              aria-label="Close dialog"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {description && (
            <p
              id={descriptionId}
              className="text-(--color-text-secondary) mb-4 text-sm"
            >
              {description}
            </p>
          )}
          {children}
        </div>
        {footer && (
          <div className="border-(--color-border) flex items-center justify-end gap-3 border-t px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
